import {
  UNMATCHED_REASON_OWNER,
  UNMATCHED_REASONS,
  type UnmatchedReason,
  type UnmatchedReasonRow,
} from "@ecomally/contracts/reports/unmatched-reasons";
import { useEffect, useRef } from "react";

import { formatCount, formatMoney } from "../lib/format.js";

/**
 * WHY THESE ORDERS ARE NOT MATCHED TO A CAMPAIGN.
 *
 * The unmatched line answers "how many", and a merchant reading it can only conclude
 * that something is broken and that it is probably somebody's fault. The orders under it
 * arrive for reasons that call for opposite responses — one is unfixable, three are the
 * agency's, one is ours — so the single number was an invitation to blame the wrong
 * party, which is what happened.
 *
 * The copy here is the report. Each row says what the signal actually was, then who can
 * act on it, in plain language rather than the snake_case key the API returns.
 */
/**
 * The click id BY NAME, per platform.
 *
 * "Click id only" was what this row said at first, and the owner went looking for the
 * word `fbclid`, could not find it, and asked whether the case was covered at all. It
 * was — under a name nobody searches for. The drawer already knows which platform it is
 * describing, so it can say the parameter the merchant actually sees in their URLs.
 */
const CLICK_ID: Record<"meta" | "google", string> = {
  meta: "fbclid",
  google: "gclid",
};

const PLATFORM_LABEL: Record<"meta" | "google", string> = {
  meta: "Meta",
  google: "Google",
};

const copyFor = (
  platform: "meta" | "google",
): Record<UnmatchedReason, { title: string; what: string; fix: string }> => ({
  ...COPY,
  click_id_only: {
    title: `Only ${CLICK_ID[platform]}, no UTM`,
    what: `A real visit carrying ${PLATFORM_LABEL[platform]}'s own click id (${CLICK_ID[platform]}) and nothing else. The ad has no tracking template at all.`,
    fix:
      platform === "meta"
        ? "Ask the agency to add a tracking template to these ads."
        : "Set a final URL suffix on the Google Ads account, or a tracking template on the campaign.",
  },

  /**
   * GOOGLE DOES NOT NEED A CAMPAIGN ID IN THE URL, and saying it does blames the wrong
   * person.
   *
   * The owner caught this on 2026-09-19: the drawer reported 23 Google orders as "UTMs
   * arrived, campaign id missing — ask the agency to add the campaign id", while the UTM
   * Checker reported ZERO incorrect Google ads. The checker was right.
   *
   * `resolvedGoogleCampaignId` resolves a campaign through FIVE tiers, and only the first
   * two read a URL parameter:
   *
   *   1. google_campaign_id   (URL)
   *   2. utm_campaign_id      (URL)
   *   3. gacmap   — from the click's ad id
   *   4. gagcmap  — from the click's ad group id
   *   5. gcmap    — from the campaign NAME in utm_campaign
   *
   * So a correctly configured Google ad routinely sends no campaign id at all, and is
   * matched from tier 3, 4 or 5. Reaching this reason means all five missed — most often
   * because the name in `utm_campaign` matches no campaign in our synced Google Ads data,
   * or the ad itself is not in that data. That is OUR sync to fix, not the agency's
   * template. Meta has no such fallback: `utm_campaign_id` is the only route, so for Meta
   * the original wording is correct and stays.
   */
  utm_without_campaign_id:
    platform === "meta"
      ? COPY.utm_without_campaign_id
      : {
          title: "UTMs arrived, campaign not resolved",
          what: "UTMs arrived, but none of them identified the campaign — and the campaign name they carried matched nothing in our synced Google Ads data. Google does not require a campaign id in the URL, so this is not a template fault.",
          fix: "Usually ours: the campaign or ad is missing from our Google Ads sync, or its name does not match. Check the UTM Checker before raising it with the agency — if it reports these ads as correct, they are.",
        },
});

const COPY: Record<UnmatchedReason, { title: string; what: string; fix: string }> = {
  no_visit_data: {
    title: "Tracker never fired",
    what: "The order reached us, but the visit never did — an ad blocker, an in-app browser, or a script that failed to load.",
    fix: "Nothing to fix. Every store has some of these; it is the floor, not a defect.",
  },
  click_id_only: {
    title: "Click id only, no UTM",
    what: "A real visit carrying only Meta's or Google's own click id. The ad has no UTM tracking template at all.",
    fix: "Ask the agency to add a tracking template to these ads.",
  },
  utm_without_campaign_id: {
    title: "UTMs arrived, campaign id missing",
    what: "The ad has a tracking template, but it does not include the campaign id parameter.",
    fix: "Ask the agency to add the campaign id to the template. Cheapest fix of the four.",
  },
  campaign_id_unmatched: {
    title: "Campaign id we do not recognise",
    what: "A campaign id did arrive and matched nothing in the ad data we hold — usually an ad account nobody connected, or a campaign newer than the last sync.",
    fix: "Ours to fix: connect the missing ad account, or wait for the next sync.",
  },
  agency_code: {
    title: "Agency code instead of campaign id",
    what: "The link carried a partner's or agency's own code (for example 97757_v0_s00…) where the campaign id belongs. No campaign has that code, so the order cannot be placed.",
    fix: "Ask whoever builds your ad links to send the campaign id instead — {{campaign.id}} in utm_id for Meta.",
  },
  campaign_id_malformed: {
    title: "Campaign id cut off or removed",
    what: "A campaign id arrived, but cut short to a few digits or replaced by --sanitized--, so it cannot be any real campaign.",
    fix: "Ask the agency to check how the links for these ads are built or shortened.",
  },
  macro_not_expanded: {
    title: "Broken tracking template",
    what: "The campaign id arrived as the literal text {{campaign.id}} — the ad platform never filled it in.",
    fix: "Ask the agency to correct the template on these ads.",
  },
};

const OWNER_LABEL: Record<"nobody" | "agency" | "you", string> = {
  nobody: "Not fixable",
  agency: "Agency",
  you: "Us",
};

export function UnmatchedReasonsDrawer({
  platform,
  rows,
  loading,
  error,
  onClose,
}: {
  readonly platform: "meta" | "google";
  readonly rows: readonly UnmatchedReasonRow[] | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
}): React.JSX.Element {
  const closeRef = useRef<HTMLButtonElement>(null);

  /**
   * Focus the close button on open and close on Escape.
   *
   * This is a panel over the page with no dialog primitive behind it — the codebase has
   * no Radix dialog and adding one for five rows is not worth a dependency — so the two
   * behaviours a keyboard user will actually reach for are wired by hand.
   */
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const total = (rows ?? []).reduce((sum, row) => sum + Number(row.orders), 0);
  const copy = copyFor(platform);
  const platformLabel = PLATFORM_LABEL[platform];

  /**
   * ALL SEVEN REASONS, ALWAYS — including the ones at zero.
   *
   * They used to be filtered to whatever the query returned, and that made a zero
   * indistinguishable from a case the drawer does not handle. The owner hit exactly that
   * on 2026-09-18: looking for orders that arrived with only an fbclid, finding no such
   * row, and having to ask whether it was covered. "0" answers that; an absent row does
   * not.
   *
   * Ordered by the vocabulary rather than by size. `UNMATCHED_REASONS` runs from "nobody
   * can fix this" to "someone can fix this today", so reading top to bottom meets the
   * unavoidable share first — and the order stays put between date ranges instead of
   * reshuffling as if the largest were always the most urgent.
   */
  const ordered = UNMATCHED_REASONS.map((reason) => ({
    reason,
    row: (rows ?? []).find((r) => r.reason === reason),
  }));

  return (
    <aside
      aria-label={`Why ${platformLabel} orders are not matched to a campaign`}
      className="bg-card fixed top-0 right-0 z-50 flex h-full w-[28rem] max-w-[calc(100vw-1rem)] flex-col border-l shadow-xl"
    >
      <header className="flex items-start gap-2 border-b px-4 py-3">
        <div className="flex-1">
          <p className="text-sm font-medium">Not matched to a campaign</p>
          <p className="text-muted-foreground text-xs">
            {platformLabel} · {formatCount(total)} orders, by last click
          </p>
        </div>
        <button
          ref={closeRef}
          type="button"
          aria-label="Close"
          className="text-muted-foreground hover:text-foreground rounded px-2 py-1 text-sm"
          onClick={onClose}
        >
          ✕
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {loading && <p className="text-muted-foreground text-sm">Loading…</p>}
        {error !== null && <p className="text-destructive text-sm">{error}</p>}
        {!loading && error === null && total === 0 && (
          <p className="text-muted-foreground mb-2 text-sm">
            Nothing unmatched in this date range — every order matched a campaign.
          </p>
        )}

        {!loading &&
          error === null &&
          total > 0 &&
          ordered.map(({ reason, row }) => {
            const orders = Number(row?.orders ?? 0);
            // Guarded: a window with nothing unmatched would divide by zero and print NaN%.
            const share = total === 0 ? 0 : Math.round((orders / total) * 100);
            const text = copy[reason];
            return (
              <section key={reason} className="border-b py-3 last:border-b-0">
                <div className="flex items-baseline gap-2">
                  <p className="flex-1 text-sm font-medium">{text.title}</p>
                  <p className="text-sm tabular-nums">{formatCount(orders)}</p>
                  <p className="text-muted-foreground w-10 text-right text-xs tabular-nums">
                    {String(share)}%
                  </p>
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  {formatMoney(row?.revenue ?? 0)} in revenue
                </p>
                <p className="mt-1.5 text-xs">{text.what}</p>
                <p className="text-muted-foreground mt-1 text-xs">
                  <span className="font-medium">{OWNER_LABEL[UNMATCHED_REASON_OWNER[reason]]}:</span>{" "}
                  {text.fix}
                </p>
              </section>
            );
          })}
      </div>
    </aside>
  );
}
